import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import SalePageNavbar from '../../components/SalePageNavbar';
import AuthModal from '../../components/AuthModal';
import { sendEmailVerification } from 'firebase/auth';
import { 
  Package, 
  CreditCard, 
  Building2, 
  Upload, 
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Tag,
  X
} from 'lucide-react';
import { collection, addDoc, serverTimestamp, getDocs, doc, getDoc, query, where, updateDoc, increment, setDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../../lib/firebase';
import { uploadPaymentSlipToR2 } from '../../services/r2Service';

export default function SalePageCheckoutPayment() {
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checkoutData, setCheckoutData] = useState<any>(null);
  const [isFreeTrialMode, setIsFreeTrialMode] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'promptpay' | 'bank'>('promptpay');
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState<{id: string, code: string, percentage: number, amount: number} | null>(null);
  const [discountError, setDiscountError] = useState('');
  const [checkingDiscount, setCheckingDiscount] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(() => {
    // Check if this is FREE trial mode from URL parameter
    const planParam = searchParams.get('plan');
    if (planParam === 'free') {
      if (!user) {
        // Show AuthModal instead of redirecting to /login
        setShowAuthModal(true);
        setIsFreeTrialMode(true);
        setCheckoutData({
          plan: 'free',
          planName: 'FREE',
          price: 0,
          isRenewal: false
        });
        return;
      }
      
      setIsFreeTrialMode(true);
      setCheckoutData({
        plan: 'free',
        planName: 'FREE',
        price: 0,
        isRenewal: false
      });
      return;
    }

    if (!user) {
      // Save checkout data to sessionStorage before redirecting
      const locationState = window.history.state;
      if (locationState?.amount !== undefined) {
        sessionStorage.setItem('pendingCheckoutData', JSON.stringify(locationState));
      }
      navigate('/login?redirect=/salepage/checkout-payment');
      return;
    }

    // Check if data is passed from checkout page state
    const locationState = window.history.state;
    let checkoutDataFromState = null;
    
    if (locationState?.amount !== undefined) {
      // Data passed from checkout page
      checkoutDataFromState = {
        plan: locationState.plan || (locationState.hasPathUrl || locationState.hasSubdomain ? 'premium' : 'basic'),
        planName: locationState.planName || (locationState.plan === 'premium' ? 'Premium' : 'Basic'),
        price: locationState.amount,
        billingPeriod: locationState.billingPeriod || 'monthly',
        hasPathUrl: locationState.hasPathUrl || false,
        hasSubdomain: locationState.hasSubdomain || false,
        username: locationState.username || '',
        isRenewal: false
      };
    } else {
      // Check if there's pending checkout data from sessionStorage (after login redirect)
      const pendingData = sessionStorage.getItem('pendingCheckoutData');
      if (pendingData) {
        const parsedData = JSON.parse(pendingData);
        checkoutDataFromState = {
          plan: parsedData.plan || (parsedData.hasPathUrl || parsedData.hasSubdomain ? 'premium' : 'basic'),
          planName: parsedData.planName || (parsedData.plan === 'premium' ? 'Premium' : 'Basic'),
          price: parsedData.amount,
          billingPeriod: parsedData.billingPeriod || 'monthly',
          hasPathUrl: parsedData.hasPathUrl || false,
          hasSubdomain: parsedData.hasSubdomain || false,
          username: parsedData.username || '',
          isRenewal: false
        };
        // Clear sessionStorage after using it
        sessionStorage.removeItem('pendingCheckoutData');
      }
    }
    
    if (checkoutDataFromState) {
      setCheckoutData(checkoutDataFromState);
      // Store in localStorage for persistence
      localStorage.setItem('salePageCheckout', JSON.stringify(checkoutDataFromState));
      loadPaymentSettings();
      return;
    }

    // Fallback to localStorage
    const data = localStorage.getItem('salePageCheckout');
    if (!data) {
      navigate('/salepage/renew');
      return;
    }

    setCheckoutData(JSON.parse(data));
    loadPaymentSettings();
  }, [user, navigate, searchParams]);

  const loadPaymentSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'salePageSettings', 'payment'));
      if (settingsDoc.exists()) {
        setPaymentSettings(settingsDoc.data());
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setPaymentProof(e.target.files[0]);
    }
  };

  const handleApplyDiscount = async () => {
    setDiscountError('');
    setCheckingDiscount(true);

    try {
      const code = discountCode.trim().toUpperCase();
      
      const discountsQuery = query(
        collection(db, 'salePageDiscounts'),
        where('code', '==', code),
        where('isActive', '==', true)
      );
      
      const discountsSnap = await getDocs(discountsQuery);
      
      if (!discountsSnap.empty) {
        const discountDoc = discountsSnap.docs[0];
        const discount = discountDoc.data();
        
        // Check expiration date
        if (discount.expirationDate) {
          const expirationDate = discount.expirationDate.toDate();
          const now = new Date();
          
          if (expirationDate < now) {
            setDiscountError('รหัสส่วนลดนี้หมดอายุแล้ว');
            setAppliedDiscount(null);
            setCheckingDiscount(false);
            return;
          }
        }
        
        const discountAmount = Math.round(checkoutData.price * (discount.percentage / 100));
        
        setAppliedDiscount({
          id: discountDoc.id,
          code: discount.code,
          percentage: discount.percentage,
          amount: discountAmount
        });
        setDiscountError('');
      } else {
        setDiscountError('รหัสส่วนลดไม่ถูกต้องหรือหมดอายุแล้ว');
        setAppliedDiscount(null);
      }
    } catch (error) {
      console.error('Error applying discount:', error);
      setDiscountError('เกิดข้อผิดพลาดในการตรวจสอบรหัสส่วนลด');
      setAppliedDiscount(null);
    } finally {
      setCheckingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const calculateFinalPrice = () => {
    if (appliedDiscount) {
      return checkoutData.price - appliedDiscount.amount;
    }
    return checkoutData.price;
  };

  const handleStartFreeTrial = async () => {
    if (!user?.uid || !user?.email) {
      alert('กรุณาเข้าสู่ระบบก่อน');
      return;
    }

    if (!auth.currentUser) {
      alert('กรุณาเข้าสู่ระบบใหม่อีกครั้ง');
      return;
    }

    setUploading(true);

    try {
      // Check if user already has a subscription
      const subscriptionRef = doc(db, 'salePageSubscriptions', user.uid);
      const subscriptionDoc = await getDoc(subscriptionRef);

      if (subscriptionDoc.exists()) {
        const subData = subscriptionDoc.data();
        if (subData.isTrialUsed) {
          alert('คุณเคยใช้ทดลองฟรีไปแล้ว');
          return;
        }
      }

      const now = Timestamp.now();

      const trialEnd = Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)); // 7 days
      
      await setDoc(subscriptionRef, {
        userId: user.uid,
        email: user.email,
        status: 'pending_email_verification',
        plan: 'free',
        trialStartDate: now,
        trialEndDate: trialEnd,
        isTrialUsed: true,
        subscriptionStartDate: null,
        subscriptionEndDate: null,
        billingPeriod: null,
        hasPathUrl: false,
        hasSubdomain: false,
        username: null,
        lastPaymentDate: null,
        lastPaymentAmount: null,
        createdAt: now,
        updatedAt: now,
      });

      const verificationUrl = `${window.location.origin}/verify-trial?product=salepage&uid=${user.uid}`;
      await sendEmailVerification(auth.currentUser, {
        url: verificationUrl,
        handleCodeInApp: false,
      });

      await addDoc(collection(db, 'admin_notifications'), {
        type: 'salepage_trial_signup',
        title: 'ผู้ใช้ใหม่เริ่มทดลองใช้ฟรี (Salepage)',
        message: `มีผู้ใช้เริ่มทดลองใช้ฟรี 7 วัน: ${user.email}`,
        userId: user.uid,
        userName: user.email,
        userEmail: user.email,
        read: false,
        createdAt: serverTimestamp(),
      });

      setShowSuccess(true);

      setTimeout(() => {
        navigate('/salepage/dashboard');
      }, 3000);
    } catch (error) {
      console.error('Error starting free trial:', error);
      alert('เกิดข้อผิดพลาดในการสมัครใช้งาน กรุณาลองใหม่อีกครั้ง');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmitPayment = async () => {
    if (!paymentProof) {
      alert('กรุณาอัปโหลดหลักฐานการชำระเงิน');
      return;
    }

    if (!user?.uid) {
      alert('กรุณาเข้าสู่ระบบก่อนทำรายการ');
      return;
    }

    setUploading(true);

    try {
      console.log('📤 Uploading payment slip to R2...');
      const slipImageUrl = await uploadPaymentSlipToR2(paymentProof, user.uid);
      console.log('✅ Payment slip uploaded:', slipImageUrl);

      console.log('💾 Saving payment notification to Firestore...');
      await addDoc(collection(db, 'salePagePaymentNotifications'), {
        userId: user.uid,
        userEmail: user.email,
        plan: checkoutData.plan,
        planName: checkoutData.planName,
        amount: checkoutData.price,
        finalAmount: calculateFinalPrice(),
        discountCode: appliedDiscount?.code || null,
        discountPercentage: appliedDiscount?.percentage || null,
        discountAmount: appliedDiscount?.amount || null,
        paymentMethod,
        isRenewal: checkoutData.isRenewal || false,
        status: 'pending',
        createdAt: serverTimestamp(),
        slipImageUrl: slipImageUrl,
        paymentProofName: paymentProof.name,
      });
      console.log('✅ Payment notification saved successfully');

      if (appliedDiscount?.id) {
        await updateDoc(doc(db, 'salePageDiscounts', appliedDiscount.id), {
          usageCount: increment(1)
        });
      }

      setShowSuccess(true);
      localStorage.removeItem('salePageCheckout');

      setTimeout(() => {
        navigate('/salepage/renew');
      }, 3000);
    } catch (error) {
      console.error('❌ Error submitting payment:', error);
      alert('เกิดข้อผิดพลาดในการส่งข้อมูล: ' + (error instanceof Error ? error.message : 'กรุณาลองใหม่อีกครั้ง'));
    } finally {
      setUploading(false);
    }
  };

  if (!checkoutData) {
    return (
      <>
        <SalePageNavbar />
        <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลด...</p>
          </div>
        </div>
      </>
    );
  }

  if (showSuccess) {
    return (
      <>
        <SalePageNavbar />
        <div className="min-h-screen bg-[#F3F8F8] flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            {isFreeTrialMode ? (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">สมัครสำเร็จ!</h2>
                <p className="text-gray-600 mb-6">
                  เราส่งอีเมลยืนยันตัวตนไปให้แล้ว<br />
                  กรุณายืนยันอีเมลเพื่อเริ่มทดลองใช้ฟรี 7 วัน
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  หากไม่พบอีเมล กรุณาตรวจสอบใน “อีเมลขยะ/สแปม”
                </p>
                <p className="text-xs text-amber-600 font-semibold mb-3">
                  เมื่อยืนยันแล้ว ระบบจะพาไปที่ Dashboard ให้ทันที
                </p>
                <p className="text-xs text-gray-500">
                  หากพบปัญหา ติดต่อแอดมินได้ที่ LINE: <a href="https://line.me/ti/p/~@edumi" target="_blank" rel="noopener noreferrer" className="font-semibold underline">@edumi</a>
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-900 mb-4">แจ้งชำระเงินสำเร็จ!</h2>
                <p className="text-gray-600 mb-6">
                  เราได้รับการแจ้งชำระเงินของคุณแล้ว<br />
                  ทีมงานจะตรวจสอบภายใน 24 ชั่วโมง
                </p>
                <p className="text-sm text-gray-500">
                  คุณจะได้รับอีเมลยืนยันเมื่อการชำระเงินได้รับการอนุมัติ
                </p>
              </>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SalePageNavbar />
      <div className="min-h-screen bg-[#F3F8F8] py-8 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <button
            onClick={() => navigate('/salepage/renew')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            กลับ
          </button>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              {isFreeTrialMode ? <Package className="w-8 h-8 text-emerald-600" /> : <CreditCard className="w-8 h-8 text-emerald-600" />}
              {isFreeTrialMode ? 'สรุปการสมัคร' : 'สรุปการชำระเงิน'}
            </h1>
            <p className="text-gray-600 mt-2">
              {isFreeTrialMode ? 'ตรวจสอบรายการและเริ่มใช้งาน' : 'ตรวจสอบรายการและชำระเงิน'}
            </p>
          </div>

          {isFreeTrialMode ? (
            // FREE Trial Mode - Simple UI
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">รายการที่เลือก</h2>
                
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-300 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 mb-2">แพ็คเก็จ Salepage</p>
                    <h3 className="text-3xl font-bold text-green-600 mb-2">FREE</h3>
                    <p className="text-lg font-semibold text-gray-900 mb-4">ทดลองใช้ฟรี 7 วัน</p>
                    <div className="bg-white rounded-lg p-4">
                      <p className="text-2xl font-bold text-gray-900">ยอดรวม: 0 บาท</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-semibold mb-2">ขั้นตอนการเริ่มใช้งาน:</p>
                      <ol className="list-decimal list-inside space-y-1 text-xs">
                        <li>กดปุ่ม "เริ่มใช้งาน" เพื่อสมัครสมาชิก</li>
                        <li>ระบบส่งอีเมลยืนยันตัวตนให้ทันที</li>
                        <li>กดยืนยันตัวตนในอีเมล (ตรวจสอบอีเมลขยะ/สแปมด้วย)</li>
                        <li>หลังยืนยันแล้ว จะเริ่มทดลองใช้ฟรี 7 วัน และเข้า Dashboard ได้ทันที</li>
                      </ol>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleStartFreeTrial}
                  disabled={uploading}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    uploading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white hover:shadow-xl'
                  }`}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังสมัคร...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      เริ่มใช้งาน
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  กดปุ่มเพื่อสมัครใช้งานฟรี 7 วัน
                </p>
              </div>
            </div>
          ) : (
            // Paid Plan Mode - Full Payment UI
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left Column - Order Summary */}
              <div className="lg:col-span-2 space-y-6">
                {/* Order Details */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5 text-emerald-600" />
                    รายการที่เลือก
                  </h2>

                  <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-xl p-4 border-2 border-emerald-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-600">แพ็คเก็จ Salepage</p>
                        <h3 className="text-lg font-bold text-gray-900">{checkoutData.planName}</h3>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-emerald-600">
                          ฿{(checkoutData.price || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-gray-500">/ เดือน</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Discount Code Section */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Tag className="w-5 h-5 text-emerald-600" />
                  รหัสส่วนลด
                </h2>

                {appliedDiscount ? (
                  <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{appliedDiscount.code}</p>
                          <p className="text-sm text-green-700">ส่วนลด {appliedDiscount.percentage}% (฿{(appliedDiscount.amount || 0).toLocaleString()})</p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveDiscount}
                        className="p-2 hover:bg-red-100 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5 text-red-600" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                        onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                        placeholder="กรอกรหัสส่วนลด"
                        className="flex-1 px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-emerald-500 focus:outline-none font-semibold"
                      />
                      <button
                        onClick={handleApplyDiscount}
                        disabled={!discountCode.trim() || checkingDiscount}
                        className={`px-6 py-3 rounded-xl font-bold transition-all ${
                          !discountCode.trim() || checkingDiscount
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-emerald-600 to-cyan-600 text-white hover:from-emerald-700 hover:to-cyan-700'
                        }`}
                      >
                        {checkingDiscount ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        ) : (
                          'ใช้'
                        )}
                      </button>
                    </div>
                    {discountError && (
                      <div className="flex items-center gap-2 text-red-600 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        {discountError}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Method */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-emerald-600" />
                  วิธีการชำระเงิน
                </h2>

                <div className="space-y-3 mb-6">
                  <button
                    onClick={() => setPaymentMethod('promptpay')}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'promptpay'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'promptpay' ? 'border-emerald-500' : 'border-gray-300'
                      }`}>
                        {paymentMethod === 'promptpay' && (
                          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">PromptPay</p>
                        <p className="text-sm text-gray-600">โอนผ่าน QR Code</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => setPaymentMethod('bank')}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMethod === 'bank'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-gray-200 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'bank' ? 'border-emerald-500' : 'border-gray-300'
                      }`}>
                        {paymentMethod === 'bank' && (
                          <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                        )}
                      </div>
                      <div>
                        <p className="font-bold text-gray-900">โอนเงินผ่านธนาคาร</p>
                        <p className="text-sm text-gray-600">โอนเข้าบัญชีธนาคาร</p>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Payment Details */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-gray-600" />
                    <h3 className="font-bold text-gray-900">ข้อมูลการโอนเงิน</h3>
                  </div>
                  {paymentMethod === 'promptpay' ? (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">PromptPay ID:</span>
                        <span className="font-semibold text-gray-900">{paymentSettings?.promptpay?.id || '0XX-XXX-XXXX'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ชื่อบัญชี:</span>
                        <span className="font-semibold text-gray-900">{paymentSettings?.promptpay?.name || 'EduMi Platform'}</span>
                      </div>
                      {paymentSettings?.promptpay?.qrCodeUrl && (
                        <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                          <p className="text-xs text-gray-600 mb-2 text-center">สแกน QR Code เพื่อชำระเงิน</p>
                          <img 
                            src={paymentSettings.promptpay.qrCodeUrl} 
                            alt="PromptPay QR Code" 
                            className="w-48 h-48 object-contain mx-auto"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">ธนาคาร:</span>
                        <span className="font-semibold text-gray-900">{paymentSettings?.bank?.bankName || 'ธนาคารกสิกรไทย'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">เลขที่บัญชี:</span>
                        <span className="font-semibold text-gray-900">{paymentSettings?.bank?.accountNumber || 'XXX-X-XXXXX-X'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">ชื่อบัญชี:</span>
                        <span className="font-semibold text-gray-900">{paymentSettings?.bank?.accountName || 'EduMi Platform'}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Upload Payment Proof */}
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    อัปโหลดหลักฐานการชำระเงิน
                  </p>
                  <p className="text-xs text-gray-500 mb-4">
                    รองรับไฟล์ JPG, PNG (ขนาดไม่เกิน 5MB)
                  </p>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    id="payment-proof"
                  />
                  <label
                    htmlFor="payment-proof"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 cursor-pointer transition-all"
                  >
                    <Upload className="w-4 h-4" />
                    เลือกไฟล์
                  </label>
                  {paymentProof && (
                    <p className="text-sm text-green-600 mt-3 flex items-center justify-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      {paymentProof.name}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Summary & Submit */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl shadow-lg p-6 border-2 border-emerald-200 sticky top-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">สรุปยอดชำระ</h2>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">แพ็คเกจ {checkoutData.planName}</span>
                    <span className="font-semibold text-gray-900">
                      {checkoutData.plan === 'premium' ? '249' : '149'}฿
                    </span>
                  </div>
                  {checkoutData.hasPathUrl && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Path URL</span>
                      <span className="font-semibold text-gray-900">+49฿</span>
                    </div>
                  )}
                  {checkoutData.hasSubdomain && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subdomain</span>
                      <span className="font-semibold text-gray-900">+99฿</span>
                    </div>
                  )}
                  {checkoutData.hasPathUrl && checkoutData.hasSubdomain && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600">ส่วนลด Bundle</span>
                      <span className="font-semibold text-green-600">-19฿</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">ราคาเต็ม</span>
                    <span className="font-semibold text-gray-900">฿{checkoutData.price !== null && checkoutData.price !== undefined ? (checkoutData.price || 0).toLocaleString() : '0'}</span>
                  </div>
                  {appliedDiscount && (
                    <div className="flex justify-between text-sm">
                      <span className="text-green-600 flex items-center gap-1">
                        <Tag className="w-4 h-4" />
                        ส่วนลด ({appliedDiscount.percentage}%)
                      </span>
                      <span className="font-semibold text-green-600">-฿{appliedDiscount.amount !== null && appliedDiscount.amount !== undefined ? (appliedDiscount.amount || 0).toLocaleString() : '0'}</span>
                    </div>
                  )}
                  <div className="border-t border-emerald-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">ยอดรวมทั้งหมด</span>
                      <span className="text-3xl font-bold text-emerald-600">
                        ฿{(calculateFinalPrice() || 0).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 text-right mt-1">ต่อเดือน</p>
                  </div>
                </div>

                {/* Important Notes */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-amber-800">
                      <p className="font-semibold mb-1">ข้อควรทราบ:</p>
                      <ul className="list-disc list-inside space-y-1 text-xs">
                        <li>โอนเงินตามยอดที่ระบุ</li>
                        <li>อัปโหลดหลักฐานการโอนเงิน</li>
                        <li>รอการตรวจสอบภายใน 24 ชม.</li>
                        <li>จะได้รับอีเมลยืนยัน</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleSubmitPayment}
                  disabled={!paymentProof || uploading}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-bold text-lg shadow-lg transition-all ${
                    !paymentProof || uploading
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white hover:shadow-xl'
                  }`}
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      กำลังส่งข้อมูล...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      แจ้งชำระเงิน
                    </>
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center mt-4">
                  กดปุ่มเพื่อแจ้งชำระเงินและรอการตรวจสอบ
                </p>
              </div>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Auth Modal for FREE trial */}
      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            // If user closes modal without login, redirect to pricing
            if (!user) {
              navigate('/salepage/pricing');
            }
          }}
          mode="register"
          signupSource="salepage"
          plan={checkoutData.plan as 'free' | 'basic' | 'premium'}
          onLoginSuccess={() => {
            // After login success, close modal and stay on this page
            // The useEffect will detect user is logged in and show checkout form
            setShowAuthModal(false);
          }}
        />
      )}
    </>
  );
}
